import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const InteractivePreview = ({ elements, pressKitData, analytics, onAnalyticsUpdate }) => {
  const [previewMode, setPreviewMode] = useState('desktop'); // desktop, tablet, mobile
  const [interactionData, setInteractionData] = useState({
    clicks: {},
    hoverTimes: {},
    scrollDepth: 0
  });
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareOptions, setShareOptions] = useState({
    publicAccess: true,
    passwordProtected: false,
    expirationDate: null,
    allowComments: true
  });

  // Preview dimensions for different devices
  const previewDimensions = {
    desktop: { width: '100%', height: '100%', scale: 1 },
    tablet: { width: '768px', height: '1024px', scale: 0.7 },
    mobile: { width: '375px', height: '667px', scale: 0.8 }
  };

  // Simulate analytics tracking
  const trackInteraction = (elementId, interactionType) => {
    setInteractionData((prev) => ({
      ...prev,
      clicks: {
        ...prev?.clicks,
        [elementId]: (prev?.clicks?.[elementId] || 0) + 1
      }
    }));

    // Update analytics with simulated engagement
    const newAnalytics = {
      ...analytics,
      engagement: Math.min(analytics?.engagement + 1, 100),
      views: analytics?.views + (Math.random() > 0.8 ? 1 : 0)
    };
    onAnalyticsUpdate?.(newAnalytics);
  };

  // Generate shareable link
  const generateShareableLink = () => {
    const baseUrl = 'https://presskit.pengamepr.com';
    const slug = pressKitData?.slug || 'press-kit';
    return `${baseUrl}/${slug}`;
  };

  // Handle element interaction in preview
  const handleElementInteraction = (element) => {
    trackInteraction(element?.id, 'click');

    // Show element-specific interaction
    switch (element?.type) {
      case 'author-bio':
        // Could open author modal or expand bio
        break;
      case 'media-gallery':
        // Could open image gallery
        break;
      case 'social-media-feed':
        // Could open social media links
        break;
    }
  };

  // Render element in preview mode
  const renderPreviewElement = (element) => {
    const style = {
      position: 'absolute',
      left: element?.position?.x,
      top: element?.position?.y,
      width: element?.size?.width,
      height: element?.size?.height,
      borderRadius: element?.style?.borderRadius || 8,
      backgroundColor: element?.style?.backgroundColor || '#ffffff',
      padding: element?.style?.padding || 16,
      boxShadow: element?.style?.boxShadow || '0 2px 4px rgba(0, 0, 0, 0.1)',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      overflow: 'hidden',
      ...element?.style
    };

    const interactionCount = interactionData?.clicks?.[element?.id] || 0;

    return (
      <div
        key={element?.id}
        style={style}
        onClick={() => handleElementInteraction(element)}
        onMouseEnter={() => trackInteraction(element?.id, 'hover')}
        className="hover:shadow-lg hover:-translate-y-1 transition-all duration-200">

        {/* Interaction Heatmap Indicator */}
        {interactionCount > 0 &&
        <div className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {interactionCount}
          </div>
        }

        {/* Element Content Based on Type */}
        {element?.type === 'author-bio' &&
        <div className="h-full flex flex-col">
            <div className="flex items-center space-x-3 mb-3">
              {element?.content?.image &&
            <img
              src={element?.content?.image}
              alt={element?.content?.imageAlt || 'Author headshot for press kit biography section'}
              className="w-12 h-12 rounded-full object-cover border-2 border-slate-200" />

            }
              <div className="flex-1">
                <h3 className="font-semibold text-slate-900 text-sm">
                  {element?.content?.name || 'Author Name'}
                </h3>
                <p className="text-xs text-slate-500">
                  {element?.content?.credentials || 'Bestselling Author'}
                </p>
              </div>
            </div>
            <p className="text-xs text-slate-600 line-clamp-4 flex-1">
              {element?.content?.bio || 'Author biography content...'}
            </p>
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-500">Click to read more</span>
              <Icon name="ExternalLink" size={12} className="text-slate-400" />
            </div>
          </div>
        }

        {element?.type === 'book-cover' &&
        <div className="h-full flex flex-col items-center justify-center">
            {element?.content?.image ?
          <div className="w-full h-full relative overflow-hidden rounded-lg">
                <img
              src={element?.content?.image}
              alt={element?.content?.imageAlt || 'Book cover image showing title and author information'}
              className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />

                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                <div className="absolute bottom-2 left-2 right-2 text-white">
                  <p className="text-xs font-medium">{element?.content?.title}</p>
                  <p className="text-xs opacity-80">{element?.content?.author}</p>
                </div>
              </div> :

          <div className="text-center">
                <Icon name="BookOpen" size={32} className="text-slate-400 mb-2" />
                <p className="text-xs text-slate-500">Book Cover Preview</p>
              </div>
          }
          </div>
        }

        {element?.type === 'press-release' &&
        <div className="h-full flex flex-col">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-2 h-2 bg-indigo-500 rounded-full" />
              <span className="text-xs font-medium text-indigo-600 uppercase tracking-wide">
                Press Release
              </span>
            </div>
            <h3 className="font-bold text-slate-900 text-sm mb-2 line-clamp-2">
              {element?.content?.headline || 'Press Release Headline'}
            </h3>
            <p className="text-xs text-slate-600 line-clamp-4 flex-1">
              {element?.content?.body || 'Press release content will be displayed here with full formatting and media contact information...'}
            </p>
            <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
              <span className="text-xs text-slate-500">Read full release</span>
              <Icon name="Download" size={12} className="text-slate-400" />
            </div>
          </div>
        }

        {element?.type === 'media-gallery' &&
        <div className="h-full">
            <div className="flex items-center space-x-2 mb-3">
              <Icon name="Images" size={14} className="text-purple-600" />
              <span className="text-xs font-medium text-purple-600 uppercase tracking-wide">
                Media Gallery
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 h-32">
              {[
            { src: "https://images.unsplash.com/photo-1596809358413-6a96dd838b80", alt: 'Professional author headshot in modern office' },
            { src: "https://images.unsplash.com/photo-1644060473538-dbbe07fd5195", alt: 'Book cover design mockup with typography' },
            { src: "https://images.unsplash.com/photo-1632791440018-68bc6422642f", alt: 'Author at book signing event with readers' },
            { src: "https://img.rocket.new/generatedImages/rocket_gen_img_174c5ec46-1762279052992.png", alt: 'Publishing house logo and branding materials' }]?.
            map((img, i) =>
            <div key={i} className="relative overflow-hidden rounded bg-slate-100 hover:shadow-md transition-shadow">
                  <img
                src={img?.src}
                alt={img?.alt}
                className="w-full h-full object-cover hover:scale-110 transition-transform duration-300" />

                </div>
            )}
            </div>
            <div className="text-center mt-2">
              <span className="text-xs text-slate-500">Click to view gallery</span>
            </div>
          </div>
        }
      </div>);

  };

  return (
    <div className="w-96 bg-white border-l border-slate-200 flex flex-col">
      {/* Preview Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-slate-900">Live Preview</h3>
          
          {/* Device Selector */}
          <div className="flex items-center space-x-1 bg-slate-100 rounded-lg p-1">
            {['desktop', 'tablet', 'mobile']?.map((device) =>
            <button
              key={device}
              onClick={() => setPreviewMode(device)}
              className={`p-2 rounded transition-colors ${
              previewMode === device ? 'bg-white shadow-sm' : ''}`
              }>

                <Icon
                name={device === 'desktop' ? 'Monitor' : device === 'tablet' ? 'Tablet' : 'Smartphone'}
                size={14}
                className="text-slate-600" />

              </button>
            )}
          </div>
        </div>

        {/* Preview Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setIsShareModalOpen(true)}
            className="flex items-center space-x-2 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm">

            <Icon name="Share" size={14} />
            <span>Share</span>
          </button>
          
          <button className="flex items-center space-x-2 px-3 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors text-sm">
            <Icon name="ExternalLink" size={14} />
            <span>Open</span>
          </button>
          
          <button className="p-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>
      </div>
      {/* Engagement Analytics */}
      <div className="p-4 border-b border-slate-200 bg-gradient-to-r from-emerald-50 to-blue-50">
        <h4 className="text-sm font-medium text-slate-900 mb-3">Real-time Engagement</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center">
            <div className="text-lg font-bold text-emerald-600">{analytics?.views}</div>
            <div className="text-xs text-slate-600">Views</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-blue-600">{analytics?.downloads}</div>
            <div className="text-xs text-slate-600">Downloads</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-purple-600">{analytics?.engagement}%</div>
            <div className="text-xs text-slate-600">Engagement</div>
          </div>
        </div>
        
        {/* Interaction Heatmap Info */}
        <div className="mt-3 p-2 bg-white/60 rounded-lg">
          <div className="flex items-center space-x-2 mb-1">
            <div className="w-3 h-3 bg-red-500 rounded-full" />
            <span className="text-xs text-slate-600">Click heatmap active</span>
          </div>
          <div className="text-xs text-slate-500">
            Total interactions: {Object?.values(interactionData?.clicks)?.reduce((a, b) => a + b, 0)}
          </div>
        </div>
      </div>
      {/* Preview Viewport */}
      <div className="flex-1 overflow-hidden bg-slate-100 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-lg shadow-lg overflow-hidden relative"
          style={{
            width: previewDimensions?.[previewMode]?.width,
            height: previewDimensions?.[previewMode]?.height,
            transform: `scale(${previewDimensions?.[previewMode]?.scale})`,
            maxWidth: '100%',
            maxHeight: '100%'
          }}>

          {/* Preview Frame */}
          <div className="relative w-full h-full overflow-auto">
            {/* Press Kit Header */}
            <div className="p-6 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
              <h1 className="text-xl font-bold mb-2">{pressKitData?.title}</h1>
              <p className="text-indigo-100 text-sm">{pressKitData?.subtitle}</p>
              <div className="flex items-center space-x-4 mt-3 text-xs text-indigo-200">
                <span>{pressKitData?.publisher}</span>
                <span>•</span>
                <span>{pressKitData?.publishDate}</span>
                <span>•</span>
                <span>{pressKitData?.genre}</span>
              </div>
            </div>

            {/* Interactive Elements */}
            <div className="relative min-h-96 bg-white">
              {elements?.map(renderPreviewElement)}
            </div>

            {/* Press Kit Footer */}
            <div className="p-6 bg-slate-50 border-t border-slate-200 text-center">
              <p className="text-sm text-slate-600 mb-2">
                For media inquiries and interview requests
              </p>
              <div className="flex items-center justify-center space-x-4 text-xs text-slate-500">
                <span>press@pengamepr.com</span>
                <span>•</span>
                <span>(555) 123-4567</span>
              </div>
            </div>
          </div>

          {/* Device Frame Decoration */}
          {previewMode !== 'desktop' &&
          <div className="absolute inset-0 pointer-events-none">
              {previewMode === 'mobile' &&
            <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-slate-300 rounded-full" />
            }
            </div>
          }
        </div>
      </div>
      {/* Preview Footer */}
      <div className="p-4 border-t border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between text-xs text-slate-600">
          <span>Preview Mode: {previewMode}</span>
          <span>Last updated: {new Date()?.toLocaleTimeString()}</span>
        </div>
        
        {/* SEO Score */}
        <div className="mt-2 flex items-center justify-between">
          <span className="text-xs text-slate-600">SEO Score:</span>
          <div className="flex items-center space-x-2">
            <div className="w-16 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div className="w-[92%] h-full bg-green-500 rounded-full" />
            </div>
            <span className="text-xs font-medium text-green-600">92/100</span>
          </div>
        </div>
      </div>
      {/* Share Modal */}
      {isShareModalOpen &&
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 max-w-[90vw]">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Share Press Kit</h3>
              <button
              onClick={() => setIsShareModalOpen(false)}
              className="p-1 hover:bg-slate-100 rounded">

                <Icon name="X" size={16} className="text-slate-500" />
              </button>
            </div>

            {/* Shareable Link */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Shareable Link
              </label>
              <div className="flex items-center space-x-2">
                <input
                type="text"
                value={generateShareableLink()}
                readOnly
                className="flex-1 px-3 py-2 text-sm border border-slate-200 rounded-lg bg-slate-50" />

                <button className="px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                  <Icon name="Copy" size={14} />
                </button>
              </div>
            </div>

            {/* Share Options */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Public Access</span>
                <button
                onClick={() => setShareOptions((prev) => ({ ...prev, publicAccess: !prev?.publicAccess }))}
                className={`w-10 h-6 rounded-full transition-colors ${
                shareOptions?.publicAccess ? 'bg-indigo-600' : 'bg-slate-300'}`
                }>

                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                shareOptions?.publicAccess ? 'translate-x-5' : 'translate-x-1'}`
                } />
                </button>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-700">Allow Comments</span>
                <button
                onClick={() => setShareOptions((prev) => ({ ...prev, allowComments: !prev?.allowComments }))}
                className={`w-10 h-6 rounded-full transition-colors ${
                shareOptions?.allowComments ? 'bg-indigo-600' : 'bg-slate-300'}`
                }>

                  <div className={`w-4 h-4 bg-white rounded-full transition-transform ${
                shareOptions?.allowComments ? 'translate-x-5' : 'translate-x-1'}`
                } />
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <button
              onClick={() => setIsShareModalOpen(false)}
              className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors">

                Cancel
              </button>
              <button className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                Share Press Kit
              </button>
            </div>
          </div>
        </div>
      }
    </div>);

};

export default InteractivePreview;